export type ProductType = 'Booster Box' | 'Booster Pack' | 'Elite Trainer Box' | 'Bundle' | 'Collector Box';

export interface Product {
  id: string;
  slug: string;
  name: string;
  game: string;
  gameSlug: string;
  set: string;
  type: ProductType;
  price: number;
  description: string;
  image: string;
  imageAlt: string;
  soldOut: boolean;
  featured: boolean;
  /** Set to open pre-orders; the product can be ordered (even when soldOut) and ships around this date. */
  preorder?: { releaseDate: string };
}

export type Availability = 'in_stock' | 'preorder' | 'sold_out';

/** Most units of one product a customer can order at once. */
export const MAX_QUANTITY_PER_PRODUCT = 10;

export interface GameCategory {
  id: string;
  slug: string;
  name: string;
  shortName: string;
  description: string;
  color: string;
  accentColor: string;
  image: string;
  imageAlt: string;
  productCount: number;
}

export const GAME_CATEGORIES: GameCategory[] = [
{
  id: 'pokemon',
  slug: 'pokemon',
  name: 'Pokémon TCG',
  shortName: 'Pokémon',
  description: 'The world\'s best-selling trading card game. Gotta catch \'em all.',
  color: '#FFCB05',
  accentColor: '#3D7DCA',
  image: "/assets/images/Pokemon_Trading_Card_Game_logo.svg-1789671838632.webp",
  imageAlt: 'Official Pokémon Trading Card Game logo',
  productCount: 3
},
{
  id: 'one-piece',
  slug: 'one-piece',
  name: 'One Piece Card Game',
  shortName: 'One Piece',
  description: 'Set sail with Luffy and crew. Bandai\'s fast-growing TCG with stunning artwork.',
  color: '#E8341C',
  accentColor: '#F4A823',
  image: "/assets/images/One_Piece_Card_Game_Logo-1789671837650.webp",
  imageAlt: 'Official One Piece Card Game logo',
  productCount: 3
},
{
  id: 'lorcana',
  slug: 'lorcana',
  name: 'Disney Lorcana',
  shortName: 'Lorcana',
  description: 'Disney magic meets strategic card play. Beautifully illustrated enchanted cards.',
  color: '#6B46C1',
  accentColor: '#F6C90E',
  image: "/assets/images/1920px-Disney_Lorcana_TCG_Logo_transparent-1789671839710.png",
  imageAlt: 'Official Disney Lorcana logo',
  productCount: 1
},
{
  id: 'riftbound',
  slug: 'riftbound',
  name: 'Riftbound',
  shortName: 'Riftbound',
  description: 'The newest challenger. Riot Games enters the TCG arena with League of Legends lore.',
  color: '#C89B3C',
  accentColor: '#0AC8B9',
  image: "/assets/images/logo_riftbound-de3d4febdd5d2d4a9e529904b3e93cff20dd3bdceee90caf3365187892c7437f-1789671837822.png",
  imageAlt: 'Official Riftbound TCG logo',
  productCount: 5
},
{
  id: 'dragon-ball',
  slug: 'dragon-ball',
  name: 'Dragon Ball Super Card Game',
  shortName: 'Dragon Ball',
  description: 'Power levels over 9000. Epic battles captured in stunning card artwork.',
  color: '#F97316',
  accentColor: '#FACC15',
  image: "/assets/images/648-6485057_logo-dragon-ball-super-tcg-leave-a-comment-1789671839026.png",
  imageAlt: 'Official Dragon Ball Super Card Game logo',
  productCount: 3
},
{
  id: 'cyberpunk',
  slug: 'cyberpunk',
  name: 'Cyberpunk 2077 TCG',
  shortName: 'Cyberpunk',
  description: 'Night City never sleeps. The dystopian future brought to life on premium cardstock.',
  color: '#FAFF00',
  accentColor: '#FF2D78',
  image: "/assets/images/cyberpunk-tcg-logo-yellow-1789672010097.webp",
  imageAlt: 'Official Cyberpunk 2077 TCG logo',
  productCount: 1
},
{
  id: 'other',
  slug: 'other',
  name: 'Other TCGs',
  shortName: 'Other TCGs',
  description: 'MTG, Flesh & Blood, Star Wars Unlimited, and more hidden gems in our vault.',
  color: '#8B5CF6',
  accentColor: '#C8A94A',
  image: "",
  imageAlt: 'Various TCG logos collection',
  productCount: 0
}];


export const ALL_PRODUCTS: Product[] = [
// ── Pokémon TCG ─────────────────────────────────────────────────
{
  id: 'pkm-mega-perfect-order-box',
  slug: 'pokemon-mega-evolution-perfect-order-booster-box',
  name: 'Mega Evolution "Perfect Order" Booster Box',
  game: 'Pokémon TCG',
  gameSlug: 'pokemon',
  set: 'Mega Evolution — Perfect Order',
  type: 'Booster Box',
  price: 119.99,
  description: 'The third set in the Pokémon TCG Mega Evolution series, released March 27, 2026. Features 124 cards (88 main + 36 secret rares) headlined by Mega Zygarde ex and Meowth ex, set in the world of Pokémon Legends: Z-A. Each booster display contains 36 packs with 10 cards + 1 Basic Energy per pack.',
  image: '/assets/images/perfectorder-1789478094760.png',
  imageAlt: 'Pokémon Mega Evolution Perfect Order booster box featuring Mega Zygarde ex artwork',
  soldOut: true,
  featured: false
},
{
  id: 'pkm-mega-pitch-black-box',
  slug: 'pokemon-mega-evolution-pitch-black-booster-box',
  name: 'Mega Evolution "Pitch Black" Booster Box',
  game: 'Pokémon TCG',
  gameSlug: 'pokemon',
  set: 'Mega Evolution — Pitch Black',
  type: 'Booster Box',
  price: 119.99,
  description: 'The fifth set in the Pokémon TCG Mega Evolution series (ME05), released July 17, 2026. Adapted from the Japanese Abyss Eye set, this 120-card set is headlined by Mega Darkrai ex alongside Mega Zeraora ex, Mega Chandelure ex, and Mega Excadrill ex. Each booster display contains 36 packs.',
  image: '/assets/images/pitchblack-1789478090964.png',
  imageAlt: 'Pokémon Mega Evolution Pitch Black booster box featuring Mega Darkrai ex artwork',
  soldOut: true,
  featured: false
},
{
  id: 'pkm-mega-chaos-rising-box',
  slug: 'pokemon-mega-evolution-chaos-rising-booster-box',
  name: 'Mega Evolution "Chaos Rising" Booster Box',
  game: 'Pokémon TCG',
  gameSlug: 'pokemon',
  set: 'Mega Evolution — Chaos Rising',
  type: 'Booster Box',
  price: 119.99,
  description: 'The fourth set in the Pokémon TCG Mega Evolution series (ME04), released May 22, 2026. Adapted from the Japanese Ninja Spinner set, this 122-card set (86 main + 36 secret rares) is headlined by Mega Greninja ex — the top chase card appearing in both SIR and Mega Hyper Rare versions. Each booster display contains 36 packs.',
  image: '/assets/images/chaosrising-1789478216444.png',
  imageAlt: 'Pokémon Mega Evolution Chaos Rising booster box featuring Mega Greninja ex artwork',
  soldOut: true,
  featured: false
},

// ── One Piece Card Game ─────────────────────────────────────────
{
  id: 'op-16-box',
  slug: 'one-piece-op16-time-of-battle-booster-box',
  name: 'OP-16 "The Time of Battle" Booster Box',
  game: 'One Piece Card Game',
  gameSlug: 'one-piece',
  set: 'The Time of Battle (OP-16)',
  type: 'Booster Box',
  price: 119.99,
  description: 'The 16th main booster set for the One Piece Card Game, released June 12, 2026. Themed around the legendary Paramount War arc, this set features characters from one of the most iconic battles in One Piece history. Each booster box contains 24 packs with 12 cards per pack.',
  image: '/assets/images/op16-1789478091194.png',
  imageAlt: 'One Piece OP-16 The Time of Battle booster box featuring Paramount War arc artwork',
  soldOut: true,
  featured: false
},
{
  id: 'op-17-box',
  slug: 'one-piece-op17-worlds-strongest-warriors-booster-box',
  name: 'OP-17 "The World\'s Strongest Warriors" Booster Box',
  game: 'One Piece Card Game',
  gameSlug: 'one-piece',
  set: 'The World\'s Strongest Warriors (OP-17)',
  type: 'Booster Box',
  price: 119.99,
  description: 'The 17th main booster set for the One Piece Card Game, released August 28, 2026. Features 131+4 card types with 6 new Leader cards — all top-tier powerhouses including Edward Newgate, Shanks, Rocks D. Xebec, Kaido, Luffy, and Big Mom. Introduces the Elbaph arc and the Giants\' homeland. Each box contains 24 packs with 12 cards per pack.',
  image: '/assets/images/op17-1789478091272.png',
  imageAlt: 'One Piece OP-17 The Worlds Strongest Warriors booster box featuring the Four Emperors artwork',
  soldOut: true,
  featured: false
},
{
  id: 'op-15-box',
  slug: 'one-piece-op15-eb04-adventure-kamis-island-booster-box',
  name: 'OP15-EB04 "Adventure on Kami\'s Island" Booster Box',
  game: 'One Piece Card Game',
  gameSlug: 'one-piece',
  set: 'Adventure on Kami\'s Island (OP15-EB04)',
  type: 'Booster Box',
  price: 119.99,
  description: 'A landmark hybrid release combining the Skypiea Arc-themed OP-15 with yellow, red, and black cards from the Japanese EB-04 extra booster, released April 3, 2026. Contains 160 unique cards with 6 new Leader cards including Enel and Monkey D. Luffy. The set\'s super alt-art Enel is the top chase card. Introduces the block rotation system for the competitive format.',
  image: '/assets/images/op15-1789478216480.png',
  imageAlt: 'One Piece OP15-EB04 Adventure on Kamis Island booster box featuring Enel and Luffy Skypiea arc artwork',
  soldOut: true,
  featured: false
},

// ── Disney Lorcana ──────────────────────────────────────────────
{
  id: 'lrc-attack-of-the-vine-box',
  slug: 'lorcana-attack-of-the-vine-booster-box',
  name: 'Disney Lorcana "Attack of the Vine!" Booster Box',
  game: 'Disney Lorcana',
  gameSlug: 'lorcana',
  set: 'Attack of the Vine!',
  type: 'Booster Box',
  price: 119.99,
  description: 'The latest Disney Lorcana expansion, released July 24, 2026. A thorny vine has invaded the Illuminary, trapping glimmers and replicating them as leafy Vinelings. Features new Shift abilities allowing two character glimmers to team up, plus Enchanted cards illustrated in a pulp movie poster art style from the \'50s–\'70s. Includes glimmers from Turning Red, Up, Monsters Inc., and more.',
  image: '/assets/images/attackofthevine-1789478288926.png',
  imageAlt: 'Disney Lorcana Attack of the Vine booster box featuring Disney characters battling a thorny vine invasion',
  soldOut: true,
  featured: false
},

// ── Riftbound ───────────────────────────────────────────────────
{
  id: 'rb-radiance-box',
  slug: 'riftbound-radiance-booster-box',
  name: 'Riftbound Radiance Booster Box',
  game: 'Riftbound',
  gameSlug: 'riftbound',
  set: 'Radiance',
  type: 'Booster Box',
  price: 119.99,
  description: 'The fourth set in the Riftbound: League of Legends TCG, releasing October 23, 2026. Features a 180-card pool with 60+ Showcase cards and introduces Evelynn alongside previously revealed champions Ekko and Seraphine. Includes a new Showdown Deck product featuring Seraphine vs. Evelynn. Each display contains 24 packs of 14 cards.',
  image: '/assets/images/riftboundradiance-1789478030582.png',
  imageAlt: 'Riftbound Radiance booster box featuring Evelynn, Ekko, and Seraphine League of Legends champion artwork',
  soldOut: true,
  featured: false
},
{
  id: 'rb-spiritforged-box',
  slug: 'riftbound-spiritforged-booster-box',
  name: 'Riftbound Spiritforged Booster Box',
  game: 'Riftbound',
  gameSlug: 'riftbound',
  set: 'Spiritforged',
  type: 'Booster Box',
  price: 119.99,
  description: 'The second set in the Riftbound: League of Legends TCG, released February 13, 2026. Features 221 base cards (308 total including 60+ alt-arts) with champions including Ahri, Irelia, Ezreal, Fiora, Rumble, and more. Introduces new Legends and mechanics. Each display contains 24 packs of 14 cards (7 Commons, 3 Uncommons, 3 Foils, 1 Token slot).',
  image: '/assets/images/riftboundspiritforged-1789478030188.png',
  imageAlt: 'Riftbound Spiritforged booster box featuring Ahri, Irelia, and Ezreal League of Legends champion artwork',
  soldOut: true,
  featured: false
},
{
  id: 'rb-unleashed-box',
  slug: 'riftbound-unleashed-booster-box',
  name: 'Riftbound Unleashed Booster Box',
  game: 'Riftbound',
  gameSlug: 'riftbound',
  set: 'Unleashed',
  type: 'Booster Box',
  price: 119.99,
  description: 'The third set in the Riftbound: League of Legends TCG, released May 8, 2026. Features 220+ cards and 30+ alt-arts, introducing four new mechanics: XP, Hunt, Ambush, and Token Battlefields. Includes 12 champions and debuts the Ultimate rarity with Baron Nashor as the first-ever Ultimate Rare. Each display contains 24 packs of 14 cards.',
  image: '/assets/images/riftboundunleashed-1789478030777.png',
  imageAlt: 'Riftbound Unleashed booster box featuring Vi and Vex League of Legends champion artwork',
  soldOut: true,
  featured: false
},
{
  id: 'rb-vendetta-box',
  slug: 'riftbound-vendetta-booster-box',
  name: 'Riftbound Vendetta Booster Box',
  game: 'Riftbound',
  gameSlug: 'riftbound',
  set: 'Vendetta',
  type: 'Booster Box',
  price: 119.99,
  description: 'The fifth set in the Riftbound: League of Legends TCG, releasing July 31, 2026. Features 160+ cards including 50+ Showcase cards and nine Champion Legends: Nasus, Renekton, Akali, Mel, Ambessa, Zed, and Shen. Introduces iconic rivalries and the first Showdown Deck product (Zed vs. Shen). Each display contains 24 packs of 14 cards.',
  image: '/assets/images/riftboundvendetta-1789478030383.png',
  imageAlt: 'Riftbound Vendetta booster box featuring Nasus, Renekton, Zed, and Shen rivalry artwork',
  soldOut: true,
  featured: false
},
{
  id: 'rb-origins-box',
  slug: 'riftbound-origins-booster-box',
  name: 'Riftbound "Origins" Booster Box',
  game: 'Riftbound',
  gameSlug: 'riftbound',
  set: 'Origins',
  type: 'Booster Box',
  price: 119.99,
  description: 'The debut set of the Riftbound: League of Legends TCG. Features nearly 300 cards (298 base) with 12 core champions including Jinx, Viktor, and Lee Sin. Each 14-card pack includes 7 Commons, 3 Uncommons, 2 foil Rares or better, 1 foil of any rarity, and 1 Token slot. Each display contains 24 packs with ~6 Epics and ~2 alt-art cards per box on average.',
  image: '/assets/images/riftboundorigins-1789478095172.png',
  imageAlt: 'Riftbound Origins booster box featuring Jinx, Viktor, and Lee Sin League of Legends champion artwork',
  soldOut: true,
  featured: false
},

// ── Dragon Ball Super Card Game ─────────────────────────────────
{
  id: 'dbs-story-booster-01',
  slug: 'dragon-ball-fusion-world-story-booster-01',
  name: 'Fusion World Story Booster 01',
  game: 'Dragon Ball Super Card Game',
  gameSlug: 'dragon-ball',
  set: 'Fusion World — Story Booster 01 (ST-01)',
  type: 'Booster Box',
  price: 119.99,
  description: 'A special story-themed booster for Dragon Ball Super Card Game Fusion World, released August 21, 2026 (English). Features 85 card types spotlighting the Majin Buu and Broly arcs, with 3 new Leader cards (Son Goten, Majin Buu, Vegeta) plus reprints of Vegito and Broly. Includes Dragon Ball-themed holographic foil Commons and 12 exclusive Energy Marker cards. Each box contains 24 packs of 12 cards.',
  image: '/assets/images/st01-1789478030580.png',
  imageAlt: 'Dragon Ball Super Card Game Fusion World Story Booster 01 box featuring Son Goten, Majin Buu, and Vegeta artwork',
  soldOut: true,
  featured: false
},
{
  id: 'dbs-fb10-box',
  slug: 'dragon-ball-fusion-world-fb10-booster-box',
  name: 'Fusion World FB10 "Cross Force" Booster Box',
  game: 'Dragon Ball Super Card Game',
  gameSlug: 'dragon-ball',
  set: 'Fusion World — Cross Force (FB10)',
  type: 'Booster Box',
  price: 119.99,
  description: 'The 10th booster set for Dragon Ball Super Card Game Fusion World, released June 13, 2026. Features 123 card types with 5 new Leader cards including Son Goku, Vegito, Son Gohan & Piccolo, Boujack, and Cell. Packed with alt-art cards and Visual Alt-Art versions of iconic Dragon Ball scenes. Each box contains 24 packs of 6 cards + 1 digital serial code per pack.',
  image: '/assets/images/fb10-1789478217087.png',
  imageAlt: 'Dragon Ball Super Fusion World FB10 Cross Force booster box featuring Vegito and Cell artwork',
  soldOut: true,
  featured: false
},
{
  id: 'dbs-fb11-box',
  slug: 'dragon-ball-fusion-world-fb11-brightness-of-hope-booster-box',
  name: 'Fusion World FB11 "Brightness of Hope" Booster Box',
  game: 'Dragon Ball Super Card Game',
  gameSlug: 'dragon-ball',
  set: 'Fusion World — Brightness of Hope (FB11)',
  type: 'Booster Box',
  price: 119.99,
  description: 'The 11th booster set for Dragon Ball Super Card Game Fusion World, releasing August 28, 2026. Features 123 card types with 5 new Leader cards including Son Goku, Future Trunks, Goku Black, Shallot/Giblet, and Bardock. Includes a crossover with the mobile game Dragon Ball Legends, featuring collaboration cards and Visual Alt-Art versions. Each box contains 24 packs of 6 cards + 1 digital serial code.',
  image: '/assets/images/fb11-1789478201208.png',
  imageAlt: 'Dragon Ball Super Fusion World FB11 Brightness of Hope booster box featuring Goku Black and Future Trunks artwork',
  soldOut: true,
  featured: false
},

// ── Cyberpunk 2077 TCG ──────────────────────────────────────────
{
  id: 'cp-welcome-night-city-set1-box',
  slug: 'cyberpunk-2077-welcome-to-night-city-set1-booster-box',
  name: '"Welcome to Night City" Set 1 Booster Box',
  game: 'Cyberpunk 2077 TCG',
  gameSlug: 'cyberpunk',
  set: 'Welcome to Night City — Set 1',
  type: 'Booster Box',
  price: 119.99,
  description: 'The debut set of the Cyberpunk TCG by WEIRDCO, releasing October 2026. Build edgerunner crews from across the Cyberpunk franchise — characters, weapons, and cyberware — and battle for Night City turf using a fast, dice-driven combat system. Each display contains 24 packs of 12 cards, with rarities including Common, Uncommon, Rare, Epic Rare, Secret Rare, and Iconic Rare (with exclusive alt-art treatments).',
  image: '/assets/images/cyberpunkset1-1789478204963.png',
  imageAlt: 'Cyberpunk TCG Welcome to Night City Set 1 booster box with neon Night City skyline and edgerunner characters',
  soldOut: true,
  featured: false
}];


export function getAvailability(product: Product): Availability {
  if (product.preorder) return 'preorder';
  return product.soldOut ? 'sold_out' : 'in_stock';
}

export function isPurchasable(product: Product): boolean {
  return getAvailability(product) !== 'sold_out';
}

export function getPreorderProducts(): Product[] {
  return ALL_PRODUCTS.filter((p) => p.preorder);
}

export function getProductById(id: string): Product | undefined {
  return ALL_PRODUCTS.find((p) => p.id === id);
}

export function getProductsByGame(gameSlug: string): Product[] {
  return ALL_PRODUCTS.filter((p) => p.gameSlug === gameSlug);
}

export function getProductBySlug(slug: string): Product | undefined {
  return ALL_PRODUCTS.find((p) => p.slug === slug);
}

export function getFeaturedProducts(): Product[] {
  return ALL_PRODUCTS.filter((p) => p.featured);
}

export function getCategoryBySlug(slug: string): GameCategory | undefined {
  return GAME_CATEGORIES.find((c) => c.slug === slug);
}